import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { stockApi, purchaseOrdersApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@verlo/shared/components/ui/select';
import { qty } from '../../lib/units';

// A supplier's products and bundles (Back office sweep, Oct 2026). Their products are the
// stock items that name them: what's in one of their packs, what it costs, and their code for
// it, each saving as you leave the box. Add one from stock, or take it off them. Their bundles
// (saved from an order) are listed to remove.

function Cell({ value, onSave, type = 'number', className, label, testId }) {
  const [v, setV] = useState(value ?? '');
  useEffect(() => { setV(value ?? ''); }, [value]);
  const commit = () => { if (String(v) !== String(value ?? '')) onSave(type === 'number' ? Number(v) || 0 : v.trim()); };
  return (
    <Input type={type} min={type === 'number' ? '0' : undefined} step={type === 'number' ? 'any' : undefined} value={v}
      onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      className={`h-8 ${type === 'number' ? 'font-mono tabular-nums' : ''} ${className || ''}`} aria-label={label} data-testid={testId} />
  );
}

export default function SupplierProducts({ supplier, stock, onStockChange }) {
  const [bundles, setBundles] = useState([]);
  const theirs = stock.filter((s) => s.supplier_id === supplier.id).sort((a, b) => a.name.localeCompare(b.name));
  const others = stock.filter((s) => s.supplier_id !== supplier.id).sort((a, b) => a.name.localeCompare(b.name));

  useEffect(() => {
    purchaseOrdersApi.bundles(supplier.id).then((r) => setBundles(r.data || [])).catch(() => setBundles([]));
  }, [supplier.id]);

  const patch = async (item, body, words) => {
    try {
      await stockApi.patch(item.id, body);
      onStockChange(item.id, body);
      if (words) toast.success(words);
    } catch { toast.error(`Couldn't save ${item.name}`); }
  };
  const removeBundle = async (b) => {
    try { await purchaseOrdersApi.removeBundle(b.id); setBundles((cur) => cur.filter((x) => x.id !== b.id)); toast.success(`${b.name} removed`); }
    catch { toast.error("Couldn't remove it"); }
  };

  return (
    <div className="space-y-5">
      <section aria-label="Products">
        <p className="mb-2 font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">Their products · {theirs.length}</p>
        <div className="overflow-x-auto rounded-lg border border-hairline">
          {theirs.length > 0 && (
            <table className="w-full min-w-[560px] text-[13.5px]">
              <thead><tr className="border-b border-hairline bg-surface-2 text-left font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">
                <th className="px-3 py-2">Item</th><th className="px-2 py-2">In a pack</th><th className="px-2 py-2">Price</th><th className="px-2 py-2">Their code</th><th className="w-20" /></tr></thead>
              <tbody>
                {theirs.map((s) => (
                  <tr key={s.id} className="border-b border-hairline last:border-0" data-testid={`supplier-product-${s.id}`}>
                    <td className="px-3 py-1.5"><div className="text-ink">{s.name}</div><div className="text-[12px] text-ink-faint">{qty(s.quantity, s.unit)} in stock</div></td>
                    <td className="px-2 py-1.5"><div className="flex items-center gap-1.5">
                      <Cell value={s.pack_size ?? 1} className="w-16" label={`${s.name} pack size`} onSave={(n) => patch(s, { pack_size: Math.max(n, 0.001) })} testId={`product-pack-${s.id}`} />
                      <span className="text-[12px] text-ink-soft">{s.unit === 'each' ? '' : s.unit}</span></div></td>
                    <td className="px-2 py-1.5"><div className="flex items-center gap-1"><span className="text-[12px] text-ink-soft">£</span>
                      <Cell value={s.cost_per_unit == null ? '' : Number(s.cost_per_unit).toFixed(2)} className="w-20" label={`${s.name} price`} onSave={(n) => patch(s, { cost_per_unit: n })} testId={`product-price-${s.id}`} />
                      <span className="text-[12px] text-ink-faint">/{s.unit === 'each' ? 'each' : s.unit}</span></div></td>
                    <td className="px-2 py-1.5"><Cell type="text" value={s.order_code || ''} className="w-28" label={`${s.name} code`} onSave={(v) => patch(s, { order_code: v })} testId={`product-code-${s.id}`} /></td>
                    <td className="pr-3 text-right"><Button variant="ghost" size="sm" className="h-8 text-ink-soft" onClick={() => patch(s, { supplier_id: null }, `${s.name} is no longer from ${supplier.name}`)}>Take off</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="flex flex-wrap items-center gap-3 px-3 py-2.5">
            <Select value="" onValueChange={(id) => { const s = stock.find((x) => x.id === id); if (s) patch(s, { supplier_id: supplier.id }, `${s.name} now comes from ${supplier.name}`); }}>
              <SelectTrigger className="h-9 w-72" data-testid="supplier-add-product"><SelectValue placeholder="+ Add a product from stock" /></SelectTrigger>
              <SelectContent>{others.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
            {!theirs.length && <span className="text-[12.5px] text-ink-faint">Nothing in stock names {supplier.name} yet.</span>}
          </div>
        </div>
      </section>

      <section aria-label="Bundles">
        <p className="mb-2 font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">Bundles · {bundles.length}</p>
        {bundles.length ? (
          <div className="divide-y divide-hairline rounded-lg border border-hairline">
            {bundles.map((b) => (
              <div key={b.id} className="flex items-center justify-between gap-3 px-3 py-2">
                <span className="min-w-0"><span className="text-ink">{b.name}</span>
                  <span className="block truncate text-[12px] text-ink-faint">{b.lines.map((l) => `${l.name || 'item'} ${qty(l.quantity)}`).join(' · ')}</span></span>
                <Button variant="ghost" size="sm" className="h-8 text-ink-soft" onClick={() => removeBundle(b)}>Remove</Button>
              </div>
            ))}
          </div>
        ) : <p className="text-[12.5px] text-ink-faint">A basket you order often. Save one from any order with “Save as a bundle”.</p>}
      </section>
      {theirs.length > 0 && <p className="text-[12px] text-ink-faint">Orders round up to whole packs. The price is what the stock is valued at, and it changes when a delivery comes in at another.</p>}
    </div>
  );
}
